import * as React from "react";
import {authorize, Overlay} from "@athena-portal/components";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import {Link} from "react-router-dom";
import {
    getAmountToDisplay,
    getAmountWithDecimalDigits,
    getSessionStorage,
    setSessionStorage
} from "../../../../../CommonFunctions";
import {
    AnnualPolicyInfo,
    AnnualPolicyPremiumTypeEnum,
    CardDetails,
    FalconNavigatorHeader,
    ICacheManager,
    ICustomerPortalService,
    MAIN_ANNUAL_PAYMENT_CARD,
    PaymentCard,
    Policy,
    PolicyProperty,
    PolicyPropertyEnum,
    productHelper,
    ProductsEnum,
    Types
} from "@falcon/services";
import {lazyInject} from "@athena-portal/core";
import FalconFooter from "../../../../../components/navigation/FalconFooter";
import {OutstandingPaymentForm} from "./OutstandingPaymentForm";
import {PanelDashed} from "../../../../../components/PanelDashed";
import {FailedPaymentMessage} from "../../../../../components/payments/FailedPaymentMessage";

interface IProps {
    match?: any
    location?: any
}

interface IState {
    premiumType?: string,
    dataLoaded: boolean,
    policy?: Policy,
    allowToUseCard?: boolean,
    directDebitSupported: boolean;
}

@authorize
export class OutstandingPaymentPage extends React.Component<IProps, IState> {
    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    @lazyInject(Types.CacheManager)
    private cacheManager: ICacheManager;

    private pluginTemplates = new PluginTemplates();

    constructor(props: IProps) {
        super(props);

        this.setPaymentAmount = this.setPaymentAmount.bind(this);

        this.state = {
            dataLoaded: false,
            directDebitSupported: false
        };
    }

    public componentDidMount() {
        this.pluginTemplates.setPageTitle("Payment");
        const product: string = this.props.location?.state?.product ?
            this.props.location?.state?.product : getSessionStorage("missedPaymentProduct");
        setSessionStorage("missedPaymentProduct", product);
        setSessionStorage("isChargeBack", true);
        setSessionStorage("missedPaymentPolicyNumber", this.props.match.params.policyNumber);
        this.cacheManager.clearCache("getPolicyHistory");

        const policyNumber: string = this.props.match.params.policyNumber;

        this.customerPortalService
            .getPolicy(policyNumber)
            .subscribe((policy: Policy) => {
                    this.setState({
                        policy
                    })
                }
            );
        this.customerPortalService.getPolicyPremiumTypeByPolicyNumber(this.props.match.params.policyNumber).subscribe((result: string) => {
            this.setState({
                premiumType: result,
                dataLoaded: true
            })
        });

        this.customerPortalService.getPolicyPropertiesByPropertyKeys(this.props.match.params.policyNumber, [MAIN_ANNUAL_PAYMENT_CARD, PolicyPropertyEnum.DIRECT_DEBIT_SUPPORTED]).subscribe(
            (properties: PolicyProperty[]) => {
                properties.forEach(property => {
                    if (property.key === MAIN_ANNUAL_PAYMENT_CARD) {
                        this.customerPortalService.getAnnualPolicyInfo(this.props.match.params.policyNumber).subscribe((annualPolicyInfo: AnnualPolicyInfo) => {
                            if (property && annualPolicyInfo && annualPolicyInfo.paymentCards) {
                                const mainCards: PaymentCard[] = annualPolicyInfo.paymentCards.filter(c => c.id.toString() === property.value);

                                if (mainCards) {
                                    this.setState({
                                        allowToUseCard: mainCards[0].reuseCard
                                    })
                                }
                            }
                        });
                    } else if (property.key === PolicyPropertyEnum.DIRECT_DEBIT_SUPPORTED) {
                        this.setState({
                            directDebitSupported: property.value === "true"
                        });
                    }
                })
            });
    }

    public render() {
        const dataLoaded = this.state.dataLoaded;
        return (
            <React.Fragment>
                <Overlay show={!dataLoaded}/>
                <FalconHeader/>
                {dataLoaded && this.renderPageContents()}
                <FalconFooter/>
            </React.Fragment>
        );
    }

    private getRedirectUrl = (): string => {
        const product: string = this.props.location?.state?.product ?
            this.props.location?.state?.product : getSessionStorage("missedPaymentProduct");
        let redirectUrl: string;

        if (product === ProductsEnum.WOLF_MOTOR || product === ProductsEnum.HOME) {
            redirectUrl = this.state.premiumType === AnnualPolicyPremiumTypeEnum.MONTHLY
                ? `/dashboard/monthlyPaymentHistory/${this.props.match.params.policyNumber}`
                : `/dashboard/annualPaymentHistory/${this.props.match.params.policyNumber}`;
        } else {
            redirectUrl = "/dashboard/paymentHistory";
        }

        return redirectUrl;
    }

    private renderPageContents(): JSX.Element {
        const {policy} = this.state;
        const product: string = this.props.location?.state?.product ? this.props.location?.state?.product : getSessionStorage("missedPaymentProduct");

        if (this.state.allowToUseCard) {
            window.sessionStorage.setItem("paymentAmount", getAmountWithDecimalDigits(policy?.annualPolicyInfo.unpaidChargebackInvoiceAmount!).toString());
            return (
                <div className="plugin">
                    {this.props.location?.state?.verifyCardFailed && <FailedPaymentMessage directDebitSupported={this.state.directDebitSupported}/>}
                    <FalconNavigatorHeader back={this.getRedirectUrl()} title="Pay off my outstanding balance"/>
                    <div className="panel">
                        <div
                            className="currency-amount-display price font-family-display font-weight-bold text-center">
                            {getAmountToDisplay(policy?.annualPolicyInfo.unpaidChargebackInvoiceAmount!)}
                        </div>
                        <div>
                            <p className="text-center font-weight-bold mb-0">to pay for outstanding balance</p>
                            <p className="text-center pt-2">All figures include Insurance Premium Tax</p>
                        </div>
                    </div>
                    <PanelDashed className="shadow bg-white text-center pay-by-card-panel">
                        <div className="panel-dashed-contents card-information">{this.renderCardInformation()}</div>
                        <div className="pb-2 or-divider"> OR</div>
                        <Link id="payNow"
                                className="text-decoration-underline cursor-pointer text-blue-1 muli-bold-16"
                                aria-label={"Pay Now"}
                                onClick={(event: any) => this.handleUseNewCard(event)}
                                to={'#'}>
                            Use a different card</Link>
                        <div className="panel-dashed-contents my-3 card-buttons">{this.renderCardsButtons()}</div>
                    </PanelDashed>
                    <button type="button"
                            className="btn btn-default btn-white-dashboard m-auto"
                            onClick={this.setPaymentAmount}>
                        Cancel changes
                    </button>
                    {this.pluginTemplates.renderBackReloadLinkWithAdditionalFunctionality("Back", true, this.getRedirectUrl(), this.setPaymentAmount)}
                </div>
            );
        } else {
            return (
                <div className="plugin pay-off-balance-new-card-page">
                    <OutstandingPaymentForm product={product}
                                            verifyNewCardFailed={false}
                    />
                </div>
            );
        }
    }

    private renderCardInformation = (): JSX.Element => {
        const card: CardDetails | null = this.findMainPaymentCard();
        if (card) {
            return (
                <div className="d-flex justify-content-center mt-3">
                    <div className="card-details-wrapper">
                        <span className="pr-2">{this.renderCardImage(card)}</span>&nbsp;
                        <span>{this.renderCardTypeName(card)}</span>&nbsp;
                        <span>ending in <b>{card.number.split('******')[1]}</b></span>
                    </div>
                </div>
            );
        } else {
            return <React.Fragment/>
        }
    };

    private renderCardImage = (card: CardDetails): JSX.Element => {
        if (card.type.includes("VISA")) {
            return <img className="payment-icon" alt="Visa icon" src="/img/visa-icon.svg"/>;
        }
        if (card.type.includes("MASTERCARD")) {
            return <img className="payment-icon" alt="Mastercard icon" src="/img/mastercard-icon.svg"/>;
        }
        return <React.Fragment/>;
    };

    private renderCardTypeName = (card: CardDetails): string => {
        if (card.type.includes("VISA")) {
            return "Visa";
        }
        if (card.type.includes("MASTERCARD")) {
            return "Mastercard";
        }
        return "";
    };

    private findMainPaymentCard(): CardDetails | null {
        let card = {} as CardDetails;
        const {policy} = this.state;
        if (policy?.interaction && productHelper.isHomeOrWolfMotor(policy?.interaction) &&
            policy?.interaction.policyHolder.cardDetails.length !== 0) {
            card = policy?.interaction.policyHolder.cardDetails[0];
            return card;
        }
        return null
    }

    private renderCardsButtons = (): JSX.Element => {
        const cardsButtons: any = [];
        const amountToPay: number = this.getAmountToPay();
        const {policy} = this.state;
        if (policy?.interaction.policyHolder.cardDetails) {
            for (const cardDetails of policy?.interaction.policyHolder.cardDetails) {
                cardsButtons.push(
                    <button key={cardDetails.number}
                            className="btn btn-primary btn-make-payment m-auto margin-top-17 justify-content-center"
                            onClick={(event: any) => this.handleOnClick(event)}
                            id={"card-" + cardDetails.number}
                            aria-label={`${getAmountToDisplay(amountToPay)} to pay`}
                    >
                        {`Pay ${getAmountToDisplay(amountToPay)}`}
                        <span className="payment-lock-image"></span>
                    </button>
                );
            }
        }
        return cardsButtons;
    };

    private getAmountToPay = (): number => {
        const {policy} = this.state;
        return policy?.annualPolicyInfo.unpaidChargebackInvoiceAmount!;
    }

    private handleOnClick(event: any): void {
        window.location.assign(`/dashboard/account/paymentProcessing/${null}/${this.state.policy?.policyNumber}`);
        event.preventDefault();
    }

    private handleUseNewCard(event: any): void {
        this.setState({
            allowToUseCard: false
        })
    }

    private setPaymentAmount(event: any): void {
        window.sessionStorage.removeItem("paymentAmount");
        window.location.href = this.getRedirectUrl();
    }
}



// WEBPACK FOOTER //
// ./src/pages/dashboard/common/payments/missedPayment/OutstandingPaymentPage.tsx